import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  useLocation, useNavigationType, useRoutes, type Location,
} from 'react-router-dom';
import gsap from 'gsap';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import pageRoutes from '@/router/pageRoutes';

const FADE_OUT = 0.18;
const FADE_IN = 0.33;
const NAV_OFFSET = 88;

const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function AppShell() {
  const location = useLocation();
  const navType = useNavigationType();

  const [displayed, setDisplayed] = useState<Location>(location);
  const containerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  const latestRef = useRef({ location, navType });
  const switchNavTypeRef = useRef(navType);
  const fadingRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const animateInRef = useRef(false);
  const prevFocusRef = useRef<Element | null>(null);
  const scrollMapRef = useRef<Map<string, number>>(new Map());
  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;

  latestRef.current = { location, navType };

  const content = useRoutes(pageRoutes, displayed);

  useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = prev;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  // Route change → fade out, then swap via timer
  useEffect(() => {
    const current = displayedRef.current;
    if (location.key === current.key) return;

    if (fadingRef.current) return; // the pending timer will pick up the latest location

    scrollMapRef.current.set(current.key, window.scrollY);

    const hashOnly = location.pathname === current.pathname && location.search === current.search;
    if (hashOnly || prefersReducedMotion() || !containerRef.current) {
      switchNavTypeRef.current = navType;
      animateInRef.current = false;
      setDisplayed(location);
      return;
    }

    fadingRef.current = true;
    prevFocusRef.current = document.activeElement;
    gsap.killTweensOf(containerRef.current);
    gsap.to(containerRef.current, { opacity: 0, duration: FADE_OUT, ease: 'power2.in' });

    timerRef.current = window.setTimeout(() => {
      fadingRef.current = false;
      timerRef.current = null;
      const latest = latestRef.current;
      switchNavTypeRef.current = latest.navType;
      animateInRef.current = true;
      setDisplayed(latest.location);
    }, FADE_OUT * 1000);
  }, [location, navType]);

  // New page mounted → set scroll before paint (so the navbar reads the right position), then fade in
  useLayoutEffect(() => {
    const isPop = switchNavTypeRef.current === 'POP';
    const saved = scrollMapRef.current.get(displayed.key);
    let raf1 = 0;
    let raf2 = 0;
    let hashTimer = 0;

    if (displayed.hash) {
      hashTimer = window.setTimeout(() => {
        const el = document.getElementById(displayed.hash.slice(1));
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET });
      }, 60);
    } else if (isPop && saved !== undefined) {
      window.scrollTo(0, saved);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => window.scrollTo(0, saved));
      });
    } else {
      window.scrollTo(0, 0);
    }
    window.dispatchEvent(new Event('scroll'));

    const el = containerRef.current;
    if (animateInRef.current && el) {
      animateInRef.current = false;
      gsap.killTweensOf(el);
      gsap.fromTo(el, { opacity: 0 }, {
        opacity: 1,
        duration: FADE_IN,
        ease: 'power2.out',
        clearProps: 'opacity',
        onComplete: () => {
          const prev = prevFocusRef.current;
          if (prev && !prev.isConnected) mainRef.current?.focus({ preventScroll: true });
          prevFocusRef.current = null;
        },
      });
    } else if (el) {
      gsap.set(el, { clearProps: 'opacity' });
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(hashTimer);
    };
  }, [displayed]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />
      <div ref={containerRef}>
        <main ref={mainRef} tabIndex={-1} className="outline-none">{content}</main>
        <Footer />
      </div>
    </div>
  );
}